import { ZfFormField, ZfIcon, ZfPopover, ZfSlider, ZfStack, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CONFIDENCE_INTERVAL_LOWER_LEVEL, DEFAULT_CONFIDENCE_INTERVAL_UPPER_LEVEL } from '../../../config/constants';
import { useGeneralSimulationSettings } from '../../../hooks/useGeneralSimulationSettings';
import { roundTo2Decimals } from '../../../utils/utils';

interface IMonteCarloGeneralDefaultSettingsProps {
    padding?: string;
}

export const MonteCarloGeneralDefaultSettings: FunctionComponent<IMonteCarloGeneralDefaultSettingsProps> = ({ padding }) => {
    const { t } = useTranslation();
    const { settings, patchSettings } = useGeneralSimulationSettings();

    const handlePercentileOptimisticChange = (event: CustomEvent<number>) => patchSettings({ percentileOptimistic: event.detail / 100 });
    const handlePercentileConservativeChange = (event: CustomEvent<number>) => patchSettings({ percentileConservative: event.detail / 100 });

    return (
        <ZfStack direction="column" spacing="small" style={{ color: 'var(--fg-color-default-normal)', padding }}>
            <ZfFormField label={t('confidenceInterval')}>
                <ZfSlider
                    value={roundTo2Decimals(
                        settings?.percentileConservative != null ? settings.percentileConservative * 100 : DEFAULT_CONFIDENCE_INTERVAL_UPPER_LEVEL,
                    )}
                    valueLabel="%"
                    min={70}
                    max={95}
                    step={5}
                    marks
                    onValueChangeCommitted={handlePercentileConservativeChange}
                    scalePosition="bottom"
                >
                    <ZfTypography slot="start" variant="label-tiny" color="normal">
                        70%
                    </ZfTypography>
                    <ZfTypography slot="end" variant="label-tiny" color="normal">
                        95%
                    </ZfTypography>
                </ZfSlider>
                <ZfPopover slot="label-trailing" alignment="start">
                    <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }} />
                    <ZfTypography variant="label-extra-small" color="normal">
                        {t('confidenceIntervalInfoBox')}
                    </ZfTypography>
                </ZfPopover>
            </ZfFormField>
            <ZfFormField>
                <ZfSlider
                    value={roundTo2Decimals(
                        settings?.percentileOptimistic != null ? settings.percentileOptimistic * 100 : DEFAULT_CONFIDENCE_INTERVAL_LOWER_LEVEL,
                    )}
                    valueLabel="%"
                    min={5}
                    max={30}
                    step={5}
                    marks
                    onValueChangeCommitted={handlePercentileOptimisticChange}
                    scalePosition="bottom"
                >
                    <ZfTypography slot="start" variant="label-tiny" color="normal">
                        5%
                    </ZfTypography>
                    <ZfTypography slot="end" variant="label-tiny" color="normal">
                        30%
                    </ZfTypography>
                </ZfSlider>
            </ZfFormField>
        </ZfStack>
    );
};
